//#region types
import { WintSuperkollAPIModelQuestionsAndAnswers } from "wint-api-client/dist/types";
import { IconDefinitionConstants, FileConstants, CommonConstants } from "utils/constants";
import { FormikProps } from "formik";
import { FormEnums, FileEnums } from "models/enums";
import { GenericFormField, GenericFormSection, Question, QuestionGroup } from "models/propTypes/genericFormProps";
//#endregion
//#region components
import ActionLinkButton from "../../buttons/actionLinkButton";
import Icon from "../../icon";
//#endregion
import { FormHelper, UtilsHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

function useDynamicFormDefinition(
  questionGroups: QuestionGroup[],
  loadFileContent?: (id: number | string) => Promise<string>
) {
  const onLinkClick = (href?: string) => {
    if (href) window.open(href, "_blank");
  };

  const getQuestion = (
    question: Question,
    hiddenWhenParentSelectsYes: boolean,
    dependantFieldId?: string
  ): GenericFormField<any> => {
    const field: GenericFormField<any> = {
      label:
        !question.description || question.description.length > CommonConstants.FormFieldPlaceholderMaxLength
          ? ""
          : question.description,
      controlLabel: question.description ?? "",
      descriptionText: (
        <>
          {<span dangerouslySetInnerHTML={{ __html: question.body ?? "" }} />}
          {question.links
            ?.sort((a, b) => a.ordinal - b.ordinal)
            .map((link) => (
              <ActionLinkButton
                key={link.ordinal}
                onClick={onLinkClick.bind(null, link.href)}
                endIcon={<Icon icon={IconDefinitionConstants.WArrowSmallRight} />}
                className={cssExports.descriptionLink}
              >
                {link.title}
              </ActionLinkButton>
            ))}
        </>
      ),
      type: FormEnums.FormFieldType.TOGGLE,
      hidden:
        question.isHidden ||
        (dependantFieldId
          ? (values: any) =>
              hiddenWhenParentSelectsYes
                ? values[FormHelper.getDynamicFormFieldName(dependantFieldId)]
                : !values[FormHelper.getDynamicFormFieldName(dependantFieldId)]
          : undefined),
    };

    switch (question.answerKind) {
      case WintSuperkollAPIModelQuestionsAndAnswers.AnswerKind.Boolean:
        field.type = FormEnums.FormFieldType.SELECTION;
        field.extraProps = {
          items: FormHelper.getBooleanSelectionItems(),
        };
        break;
      case WintSuperkollAPIModelQuestionsAndAnswers.AnswerKind.Text:
        field.type = FormEnums.FormFieldType.TEXTAREA;
        break;
      case WintSuperkollAPIModelQuestionsAndAnswers.AnswerKind.SingleLineText:
        field.type = FormEnums.FormFieldType.TEXT;
        break;
      case WintSuperkollAPIModelQuestionsAndAnswers.AnswerKind.Numeric:
        field.type = FormEnums.FormFieldType.NUMBER;
        break;
      case WintSuperkollAPIModelQuestionsAndAnswers.AnswerKind.Selection:
      case WintSuperkollAPIModelQuestionsAndAnswers.AnswerKind.StringSelection:
        const selectionDefinition =
          question.answerKind === WintSuperkollAPIModelQuestionsAndAnswers.AnswerKind.Selection
            ? question.selectionDefinition
            : question.stringSelectionDefinition;

        field.type = selectionDefinition?.multiSelection
          ? FormEnums.FormFieldType.CHECKBOXSELECTIONGROUP
          : FormEnums.FormFieldType.RADIOSELECTIONGROUP;
        field.extraProps = {
          items:
            selectionDefinition?.selectionValues?.map((s) => ({
              id: s.value,
              value: s.description ?? "",
            })) ?? [],
        };
        break;
      case WintSuperkollAPIModelQuestionsAndAnswers.AnswerKind.File:
        field.type = FormEnums.FormFieldType.FILEUPLOADER;
        field.extraProps = {
          allowedTypes: question.fileDefinition?.mediaTypes as FileEnums.ContentTypes[],
          singleSelection: question.fileDefinition?.minimumFileCount === 1,
          maxFileCount: question.fileDefinition?.maximumFileCount,
          maxFileSize: FileConstants.FileSizes.TwoMB,
          loadFileContent: loadFileContent,
        };
        break;
      case WintSuperkollAPIModelQuestionsAndAnswers.AnswerKind.Matrix:
        field.type = FormEnums.FormFieldType.EDITABLETABLE;
        const innerFields: Record<any, GenericFormField<any>> = {};
        question.matrixDefinition?.questionMatrixColumnDefinitions?.forEach((m, index) => {
          innerFields[`${m.ordinal}`] = getMatrixField(m);
        });
        field.innerFields = innerFields;
        field.extraProps = {
          idFieldKey: `uniqueId`,
          defaultRow: {},
        };
        break;
    }
    return field;
  };

  const getMatrixField = (
    column: WintSuperkollAPIModelQuestionsAndAnswers.QuestionMatrixColumnDefinitionModel
  ): GenericFormField<any> => {
    const field: GenericFormField<any> = {
      label: column.name ?? "",
      type: FormEnums.FormFieldType.TOGGLE,
    };

    switch (column.valueKind) {
      case WintSuperkollAPIModelQuestionsAndAnswers.QuestionMatrixValueKind.Boolean:
        field.type = FormEnums.FormFieldType.TOGGLE;
        break;
      case WintSuperkollAPIModelQuestionsAndAnswers.QuestionMatrixValueKind.Text:
        field.type = FormEnums.FormFieldType.TEXT;
        break;
      case WintSuperkollAPIModelQuestionsAndAnswers.QuestionMatrixValueKind.Numeric:
        field.type = FormEnums.FormFieldType.NUMBER;
        break;
    }
    return field;
  };

  const onOptionalFieldChange = (name: string, updatedValue: any, formikProps: FormikProps<any>) => {
    formikProps.validateForm();
  };

  const addOptionalGroupField = (fields: Record<any, GenericFormField<any>>, questionGroup: QuestionGroup) => {
    if (questionGroup.isOptional) {
      const field: GenericFormField<any> = {
        label: questionGroup.name ?? "",
        descriptionText:  <span dangerouslySetInnerHTML={{ __html: questionGroup.description ?? "" }} />,
        type: FormEnums.FormFieldType.SELECTION,
        hidden: questionGroup.isHidden,
        extraProps: {
          items: FormHelper.getBooleanSelectionItems(),
        },
        onChangeHandler: onOptionalFieldChange,
      };
      fields[FormHelper.getDynamicFormFieldName(questionGroup.id!)] = field;
    }
  };

  const getSubSections = (group: QuestionGroup, subGroups?: QuestionGroup[]) => {
    const parentLevel = group.level ?? "/";
    const sections: GenericFormSection<any>[] = [];
    const sectionFields: Record<any, GenericFormField<any>> = {};

    if (subGroups?.length) {
      const pattern = new RegExp(`^${parentLevel}([\\w\\/]+)`);

      subGroups
        .filter((qg) => {
          const matches = qg.level?.match(pattern);
          return matches?.length ? matches[1].split("/").filter((s) => !UtilsHelper.isEmpty(s)).length === 1 : false;
        })
        .forEach((qg) => {
          sections.push(
            getSection(
              qg,
              FormEnums.FormSectionType.FIELDGROUPSECTION,
              subGroups.filter((sg) => sg.level?.startsWith(parentLevel))
            )
          );
        });
    }

    addOptionalGroupField(sectionFields, group);

    group.questions
      ?.sort((a, b) => a.ordinal - b.ordinal)
      .forEach((q) => {
        sectionFields[FormHelper.getDynamicFormFieldName(q.id!)] = getQuestion(
          q,
          q.hiddenWhenParentSelectsYes ?? false,
          group.isOptional ? group.id : q.parentId ? q.parentId : undefined
        );
      });

    return [
      ...(group.questions?.length || group.isOptional
        ? [
            {
              label: "",
              type: FormEnums.FormSectionType.FIELDGROUPSECTION,
              innerFields: sectionFields,
              className: cssExports.noTitle,
            },
          ]
        : []),
      ...(sections ?? []),
    ];
  };

  const getSection = (
    group: QuestionGroup,
    rootFormSectionType: FormEnums.FormSectionType,
    subGroups?: QuestionGroup[]
  ): GenericFormSection<any> => {
    const isInline = !group.questions?.some(
      (q) => !q.isHidden && q.answerKind === WintSuperkollAPIModelQuestionsAndAnswers.AnswerKind.Matrix
    );
    const subSections: GenericFormSection<any>[] = [];
    if (group.description && rootFormSectionType !== FormEnums.FormSectionType.FIELDGROUPSECTION) {
      subSections.push({
        label: "",
        type: FormEnums.FormSectionType.FIELDGROUPSECTION,
        descriptionText: <span dangerouslySetInnerHTML={{ __html: group.description }} />,
        innerFields: {},
        hidden: group.isHidden,
      });
    }

    subSections.push(...getSubSections(group, subGroups));

    return {
      label: group.name ?? "",
      descriptionText:
        rootFormSectionType === FormEnums.FormSectionType.FIELDGROUPSECTION && group.description ? (
          <span
            dangerouslySetInnerHTML={{
              __html: group.description,
            }}
          />
        ) : undefined,
      type: rootFormSectionType,
      inline: isInline,
      enableControlLables: !isInline,
      innerFields: subSections,
      className: UtilsHelper.isEmpty(group.name) || group.isOptional ? cssExports.noTitle : "",
      hidden: group.isHidden,
    };
  };

  const getSections = (rootFormSectionType: FormEnums.FormSectionType): GenericFormSection<any>[] => {
    const mainGroups = questionGroups.filter(
      (qg) => qg.level?.split("/").filter((s) => !UtilsHelper.isEmpty(s)).length === 1
    );

    return mainGroups
      ?.sort((a, b) => Number(a.level?.split("/")[1] ?? 0) - Number(b.level?.split("/")[1] ?? 0))
      .map((qg) => {
        const regex = new RegExp(`^${qg.level ?? "/"}([\\w\\/]+)`);
        return getSection(
          qg,
          rootFormSectionType,
          questionGroups.filter((sg) => {
            const matches = sg.level ? regex.test(sg.level) : false;
            return matches;
          })
        );
      });
  };

  return { getSections };
}

export default useDynamicFormDefinition;
